import { LayerStatus, PointArray } from '@editor/interfaces/attribute'
import { VolumeEstimationMethods } from '@editor/interfaces/inspection'
import { PlaneSide } from '@editor/interfaces/shape'
import { isNewPointOverlapping } from '@editor/services/Points'
import { findPolygonCentroid } from '@editor/services/Polygon'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Matrix4Tuple, Vector3 } from 'three'

export const PLANE_CREATION_ORDER_DEFAULT: PlaneSide[] = [PlaneSide.UPPER, PlaneSide.LOWER]
export const WORKING_PLANE_LOWER_ID = 'working-volume-polygon-lower-plane'
export const WORKING_PLANE_UPPER_ID = 'working-volume-polygon-upper-plane'
export const PANEL_WORKING_PLANE_UPPER_ID = `panel-${WORKING_PLANE_UPPER_ID}`
export const PANEL_WORKING_PLANE_LOWER_ID = `panel-${WORKING_PLANE_LOWER_ID}`

export enum DrawingStage {
  Draw,
  Height,
  Complete,
}

interface PlaneData extends LayerStatus {
  /**
   * Current set of points (after adjustments).
   */
  points: PointArray[]

  /**
   * Original set of points (before adjustments).
   */
  originalPoints: PointArray[]

  /**
   * Centroid of the plane based on original points.
   */
  originalCentroid: PointArray

  /**
   * Flag to indicate if the plane was closed by double-click.
   * This will affect how undo works, with the last point being removed if true.
   */
  isDoubleClickClosed?: boolean
}

export interface VolumeEstimationPolygonState {
  /**
   * Drawing stage.
   */
  drawingStage: DrawingStage

  /**
   * Working points when drawing polygon.
   */
  workingPoints: PointArray[]

  /**
   * Adjustments for virtual plane (top).
   */
  virtualPlaneTransformation?: Matrix4Tuple

  /**
   * Completed planes.
   */
  planes: Record<PlaneSide, PlaneData | null>

  /**
   * New volume is being saved.
   */
  isSaving: boolean

  /**
   * Existing volume is being updated.
   */
  isUpdating: boolean

  /**
   * Flag to indicate if the tool is dirty (has working items).
   */
  isDirty: boolean

  /**
   * Plane creation order.
   */
  planeCreationOrder: PlaneSide[]

  /**
   * Selected volume estimation method.
   */
  estimationMethod: VolumeEstimationMethods

  /**
   * Flag to indicate if the rebar modelled is confirmed.
   */
  rebarModelledConfirmed: boolean
}

const updatePointsAtIndex = (points: PointArray[], index: number, position: PointArray) => {
  points[index] = position

  // If first point is updated, update the last point as well, and vice versa.
  if (index === 0) {
    points[points.length - 1] = position
  }

  if (index === points.length - 1) {
    points[0] = position
  }

  return points
}

const initialState: VolumeEstimationPolygonState = {
  drawingStage: DrawingStage.Draw,
  isSaving: false,
  isUpdating: false,
  isDirty: false,
  workingPoints: [],
  planeCreationOrder: PLANE_CREATION_ORDER_DEFAULT,
  planes: {
    upper: null,
    lower: null,
  },
  estimationMethod: VolumeEstimationMethods.Standard,
  rebarModelledConfirmed: false,
}

export const slice = createSlice({
  name: 'editor/tools/volumeEstimation/polygon',
  initialState,
  reducers: {
    /**
     * Tool/Editor states
     */
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },
    setIsUpdating: (state, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload
    },
    setDrawingStage: (state, action: PayloadAction<DrawingStage>) => {
      state.drawingStage = action.payload

      if (state.drawingStage === DrawingStage.Complete) {
        state.workingPoints = []
      }
    },
    setPlaneCreationOrder: (state, action: PayloadAction<PlaneSide[]>) => {
      state.planeCreationOrder = action.payload
    },

    /**
     * Working points and planes actions
     */
    addWorkingPoint: (state, { payload }: PayloadAction<PointArray>) => {
      if (!isNewPointOverlapping(state.workingPoints, payload)) {
        state.workingPoints.push(payload)
      }
      state.isDirty = true
    },
    setWorkingPoints: (state, action: PayloadAction<PointArray[]>) => {
      state.workingPoints = action.payload
    },
    completeBottomPlane: (state, { payload: { isDoubleClicked } }: PayloadAction<{ isDoubleClicked: boolean }>) => {
      const tempVector = new Vector3()
      if (state.planes.upper) {
        state.planes.lower = {
          points: [...state.workingPoints],
          originalPoints: [...state.workingPoints],
          originalCentroid: findPolygonCentroid(state.workingPoints.map((p) => tempVector.set(...p))).toArray(),
          isDoubleClickClosed: isDoubleClicked,
        }
        state.workingPoints = []
      } else {
        state.planes.lower = {
          points: [...state.workingPoints],
          originalPoints: [...state.workingPoints],
          originalCentroid: findPolygonCentroid(state.workingPoints.map((p) => tempVector.set(...p))).toArray(),
          isDoubleClickClosed: isDoubleClicked,
        }
      }
    },
    completeTopPlane: (state, { payload: { isDoubleClicked } }: PayloadAction<{ isDoubleClicked: boolean }>) => {
      const tempVector = new Vector3()
      if (state.planes.lower) {
        state.planes.upper = {
          points: [...state.workingPoints],
          originalPoints: [...state.workingPoints],
          originalCentroid: findPolygonCentroid(state.workingPoints.map((p) => tempVector.set(...p))).toArray(),
          isDoubleClickClosed: isDoubleClicked,
        }
        state.workingPoints = []
      } else {
        state.planes.upper = {
          points: [...state.workingPoints],
          originalPoints: [...state.workingPoints],
          originalCentroid: findPolygonCentroid(state.workingPoints.map((p) => tempVector.set(...p))).toArray(),
          isDoubleClickClosed: isDoubleClicked,
        }
      }
    },
    resetWorkingPoints: (state) => {
      state.workingPoints = []
    },

    /**
     * Modifications of completed planes
     */
    // -- Top Plane --
    updateTopPlaneAnchor: (state, action: PayloadAction<{ index: number; position: PointArray }>) => {
      if (state.planes.upper) {
        updatePointsAtIndex(state.planes.upper.points, action.payload.index, action.payload.position)
      }
    },
    updateTopPlane: (state, action: PayloadAction<PointArray[]>) => {
      state.planes.upper = {
        ...state.planes.upper,
        points: action.payload,
        originalPoints: state.planes.upper?.originalPoints || action.payload,
        originalCentroid:
          state.planes.upper?.originalCentroid ||
          findPolygonCentroid(action.payload.map((p) => new Vector3(...p))).toArray(),
      }
    },
    swapPlanes: (state) => {
      const temp = state.planes.upper
      state.planes.upper = state.planes.lower
      state.planes.lower = temp
    },
    resetTopPlane: (state) => {
      state.planes.upper = null
      if (state.planeCreationOrder[0] === PlaneSide.UPPER) {
        state.drawingStage = DrawingStage.Draw
        state.workingPoints = []
      } else {
        state.drawingStage = DrawingStage.Height
      }

      if (!state.planes.lower?.points.length) state.isDirty = false
    },
    restoreTopPlane: (state) => {
      if (!state.planes.upper) return

      state.planes.upper = {
        ...state.planes.upper,
        points: state.planes.upper?.originalPoints || [],
      }
    },

    // -- Virtual top plane adjustments --
    setVirtualPlaneTransformation: (state, action: PayloadAction<Matrix4Tuple | undefined>) => {
      state.virtualPlaneTransformation = action.payload
    },

    // -- Bottom Plane --
    updateBottomPlaneAnchor: (state, action: PayloadAction<{ index: number; position: PointArray }>) => {
      if (state.planes.lower) {
        updatePointsAtIndex(state.planes.lower.points, action.payload.index, action.payload.position)
      }
    },
    updateBottomPlane: (state, action: PayloadAction<PointArray[]>) => {
      state.planes.lower = {
        ...state.planes.lower,
        points: action.payload,
        originalPoints: state.planes.lower?.originalPoints || action.payload,
        originalCentroid:
          state.planes.lower?.originalCentroid ||
          findPolygonCentroid(action.payload.map((p) => new Vector3(...p))).toArray(),
      }
    },
    resetBottomPlane: (state) => {
      state.planes.lower = null
      if (state.planeCreationOrder[0] === PlaneSide.LOWER) {
        state.drawingStage = DrawingStage.Draw
        state.workingPoints = []
      } else {
        state.drawingStage = DrawingStage.Height
      }

      if (!state.planes.upper?.points.length) state.isDirty = false
    },

    /**
     * Undo action
     */
    undoLastWorkingPoint: (state) => {
      // If we're in the complete stage, remove the last plane.
      if (state.drawingStage === DrawingStage.Complete) {
        if (state.planeCreationOrder[0] === PlaneSide.UPPER && state.planes.lower) {
          state.workingPoints = [...state.planes.lower.originalPoints]
          state.planes.lower = null
          state.drawingStage = DrawingStage.Height
        } else if (state.planeCreationOrder[0] === PlaneSide.LOWER && state.planes.upper) {
          state.workingPoints = [...state.planes.upper.originalPoints]
          state.planes.upper = null
          state.drawingStage = DrawingStage.Height
        }
      }
      // If we're in height selection, re-open the first plane.
      else if (state.drawingStage === DrawingStage.Height) {
        // If we have planes, either re-open or remove the second plane.
        if (state.planeCreationOrder[0] === PlaneSide.UPPER && state.planes.upper) {
          const { isDoubleClickClosed } = state.planes.upper
          state.workingPoints = state.planes.upper.originalPoints
          state.planes.upper = null
          state.drawingStage = DrawingStage.Draw

          if (isDoubleClickClosed) {
            state.workingPoints = state.workingPoints.slice(0, -1)
          }
        } else if (state.planeCreationOrder[0] === PlaneSide.LOWER && state.planes.lower) {
          state.workingPoints = state.planes.lower.originalPoints
          state.planes.lower = null
          state.drawingStage = DrawingStage.Draw
        }
      }
      // If we're in the drawing stage, remove the last point.
      else if (state.drawingStage === DrawingStage.Draw && state.workingPoints.length) {
        state.workingPoints = state.workingPoints.slice(0, -1)

        // If we have no more points, we're not dirty anymore.
        if (!state.workingPoints.length) state.isDirty = false
      }
    },

    /**
     * Tool options
     */
    setEstimationMethod: (state, action: PayloadAction<VolumeEstimationMethods>) => {
      state.estimationMethod = action.payload
    },
    setRebarModelledConfirmed: (state, action: PayloadAction<boolean>) => {
      state.rebarModelledConfirmed = action.payload
    },

    /**
     * Resets
     */
    reset: () => initialState,
  },
})

export const {
  // Tool/Editor states
  setIsSaving,
  setIsUpdating,
  setIsDirty,
  setDrawingStage,
  setPlaneCreationOrder,

  // Working points and planes actions
  addWorkingPoint,
  setWorkingPoints,
  completeBottomPlane,
  completeTopPlane,
  resetWorkingPoints,

  // Modifications of completed planes
  // -- Top Plane --
  updateTopPlaneAnchor,
  updateTopPlane,
  swapPlanes,
  resetTopPlane,
  restoreTopPlane,

  // -- Virtual top plane adjustments --
  setVirtualPlaneTransformation,

  // -- Bottom Plane --
  updateBottomPlaneAnchor,
  updateBottomPlane,
  resetBottomPlane,

  // Undo action
  undoLastWorkingPoint,

  // Tool options
  setEstimationMethod,
  setRebarModelledConfirmed,

  // Resets
  reset,
} = slice.actions

export default slice.reducer
